"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";

/**
 * DebugDataDisplay Component
 * URL'de debugmode=1 varsa backend'den gelen data'yı gösterir
 */
export default function DebugDataDisplay({
  sectionId,
  sectionType,
  data,
  source,
  additionalInfo = {}
}) {
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // URL'deki debugmode parametresini kontrol et
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      setIsDebugMode(urlParams.get("debugmode") === "1");
    }
  }, []);

  // Debug mode aktif değilse hiçbir şey render etme
  if (!isDebugMode) {
    return null;
  }

  // Debug bilgilerini hazırla
  const debugInfo = {
    sectionId,
    sectionType,
    dataType: Array.isArray(data) ? "array" : typeof data,
    dataLength: Array.isArray(data) ? data.length : Object.keys(data || {}).length,
    source: {
      type: source?.type || "unknown",
      hasFunc: !!source?.func,
      hasParams: !!source?.params,
      hasTimeout: !!source?.timeout,
      hasFallback: !!source?.fallbackValue
    },
    timestamp: new Date().toLocaleTimeString(),
    additionalInfo
  };

  return (
    <div className={clsx(
      "debug-data-display mt-4 border-2 border-dashed border-yellow-400 bg-yellow-50 rounded-lg p-4 container mx-auto",
      "transition-all duration-300 ease-in-out"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className="text-xs font-semibold text-yellow-800 bg-yellow-200 px-2 py-1 rounded">
            DEBUG MODE
          </span>
          <span className="text-sm font-medium text-yellow-800">
            Section: {sectionId} ({sectionType})
          </span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs bg-yellow-200 hover:bg-yellow-300 text-yellow-800 px-2 py-1 rounded transition-colors"
        >
          {isExpanded ? "Hide" : "Show"} Data
        </button>
      </div>

      {/* Debug Info Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="bg-yellow-100 p-2 rounded text-xs">
          <div className="font-medium text-yellow-800">Data Type</div>
          <div className="text-yellow-700">{debugInfo.dataType}</div>
        </div>
        <div className="bg-yellow-100 p-2 rounded text-xs">
          <div className="font-medium text-yellow-800">Length</div>
          <div className="text-yellow-700">{debugInfo.dataLength}</div>
        </div>
        <div className="bg-yellow-100 p-2 rounded text-xs">
          <div className="font-medium text-yellow-800">Source Type</div>
          <div className="text-yellow-700">{debugInfo.source.type}</div>
        </div>
        <div className="bg-yellow-100 p-2 rounded text-xs">
          <div className="font-medium text-yellow-800">Timestamp</div>
          <div className="text-yellow-700">{debugInfo.timestamp}</div>
        </div>
      </div>

      {/* Source Info */}
      <div className="bg-yellow-100 p-3 rounded mb-3">
        <div className="text-xs font-medium text-yellow-800 mb-2">Source Configuration:</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="flex items-center space-x-1">
            <span className={clsx(
              "w-2 h-2 rounded-full",
              debugInfo.source.hasFunc ? "bg-green-500" : "bg-gray-300"
            )}></span>
            <span className="text-yellow-700">Function</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className={clsx(
              "w-2 h-2 rounded-full",
              debugInfo.source.hasParams ? "bg-green-500" : "bg-gray-300"
            )}></span>
            <span className="text-yellow-700">Params</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className={clsx(
              "w-2 h-2 rounded-full",
              debugInfo.source.hasTimeout ? "bg-green-500" : "bg-gray-300"
            )}></span>
            <span className="text-yellow-700">Timeout</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className={clsx(
              "w-2 h-2 rounded-full",
              debugInfo.source.hasFallback ? "bg-green-500" : "bg-gray-300"
            )}></span>
            <span className="text-yellow-700">Fallback</span>
          </div>
        </div>
      </div>

      {/* Expandable Data Display */}
      {isExpanded && (
        <div className="space-y-4">
          {/* Raw Data */}
          <div>
            <div className="text-xs font-medium text-yellow-800 mb-2">Raw Data:</div>
            <pre className="text-xs bg-yellow-100 p-3 rounded overflow-auto max-h-64 border">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>

          {/* Source Configuration */}
          <div>
            <div className="text-xs font-medium text-yellow-800 mb-2">Source Configuration:</div>
            <pre className="text-xs bg-yellow-100 p-3 rounded overflow-auto max-h-32 border">
              {JSON.stringify(source, null, 2)}
            </pre>
          </div>

          {/* Additional Info */}
          {Object.keys(additionalInfo).length > 0 && (
            <div>
              <div className="text-xs font-medium text-yellow-800 mb-2">Additional Info:</div>
              <pre className="text-xs bg-yellow-100 p-3 rounded overflow-auto max-h-32 border">
                {JSON.stringify(additionalInfo, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-yellow-300">
        <div className="text-xs text-yellow-600">
          💡 Add <code className="bg-yellow-200 px-1 rounded">?debugmode=1</code> to URL to see this debug info
        </div>
      </div>
    </div>
  );
}
